﻿export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly traceId?: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

export type ApiRequestOptions = {
  timeoutMs?: number;
  timeoutMessage?: string;
};

export async function api<T>(
  path: string,
  init: RequestInit = {},
  options: ApiRequestOptions = {},
): Promise<T> {
  const controller = new AbortController();
  const externalSignal = init.signal;
  let timeoutFired = false;

  const abortFromExternal = () => controller.abort();
  if (externalSignal?.aborted) {
    controller.abort();
  } else {
    externalSignal?.addEventListener('abort', abortFromExternal, { once: true });
  }

  const timer = options.timeoutMs && options.timeoutMs > 0
    ? window.setTimeout(() => {
      timeoutFired = true;
      controller.abort();
    }, options.timeoutMs)
    : undefined;

  try {
    const response = await fetch(`/api${path}`, {
      credentials: 'include',
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const data = typeof body === 'object' && body !== null
        ? body as Record<string, unknown>
        : {};
      const retryHeader = Number(response.headers.get('retry-after'));
      const retryBody = Number(data.retryAfterSeconds);
      const retryAfterSeconds = Number.isFinite(retryBody) && retryBody > 0
        ? retryBody
        : Number.isFinite(retryHeader) && retryHeader > 0
          ? retryHeader
          : undefined;

      throw new ApiError(
        response.status,
        String(data.code ?? 'HTTP_ERROR'),
        String(data.message ?? body ?? `HTTP ${response.status}`),
        data.traceId ? String(data.traceId) : undefined,
        retryAfterSeconds,
      );
    }

    return body as T;
  } catch (reason) {
    if (timeoutFired) {
      throw new ApiError(
        408,
        'CLIENT_TIMEOUT',
        options.timeoutMessage ?? '请求超时，请稍后再试。',
      );
    }

    throw reason;
  } finally {
    if (timer !== undefined) {
      window.clearTimeout(timer);
    }
    externalSignal?.removeEventListener('abort', abortFromExternal);
  }
}

export type CurrentUser = {
  id: number;
  username: string;
  displayName: string;
  role: 'ADMIN' | 'USER';
};

export type PlatformHealth = {
  webReachable: boolean;
  keeperReachable: boolean;
  mediaServerReachable: boolean;
  mediaServerId?: string;
  message?: string;
};

export type LiveSession = {
  sessionId: string;
  videoSourceId: number;
  mediaUrl: string;
  expiresAt: string;
  reusedUpstream: boolean;
};

export type LiveWorkspaceClaimResult = {
  workspaceId: string;
  replacedExistingWorkspace: boolean;
  serverTime: string;
  expiresAt: string;
  closedSessionIds: string[];
  activeSessionIds: string[];
};

export type LiveWorkspaceHeartbeatResult = {
  workspaceId: string;
  isOwner: boolean;
  serverTime: string;
  expiresAt: string;
  activeSessionIds: string[];
};

export type RecordingQueryHandle = {
  taskId: number;
  videoSourceId: number;
};

export type RecordingSegment = {
  ssrcId: string;
  startTime: string;
  endTime: string;
  startUnixTimeSeconds: number;
  endUnixTimeSeconds: number;
  startLocalDateTime: string;
  endLocalDateTime: string;
  name?: string;
};

export type RecordingQueryResult = {
  taskId: number;
  queryDate: string;
  dayStartUnixTimeSeconds: number;
  utcOffsetMinutes: number;
  recordingTimeZoneId: string;
  completed: boolean;
  totalCount: number;
  receivedCount: number;
  segments: RecordingSegment[];
  message?: string;
};

export type PlaybackSession = {
  sessionId: string;
  videoSourceId: number;
  taskId: number;
  sourceSsrcId: string;
  ssrcId: string;
  offsetSeconds: number;
  mediaUrl: string;
  expiresAt: string;
};

export type PagedResult<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type PlayableArea = {
  id: number;
  parentId?: number;
  name: string;
  sortOrder: number;
  sourceCount: number;
  onlineSourceCount: number;
};

export type PlayableVideoSource = {
  id: number;
  displayName: string;
  areaId?: number;
  areaName?: string;
  sourceType: string;
  nvrDisplayName?: string;
  onlineStatus: string;
  upstreamStatus: string;
  isActivated: boolean;
  supportsRecording: boolean;
  sortOrder: number;
  lastSyncAt?: string;
};

export type AdminArea = {
  id: number;
  parentId?: number;
  name: string;
  sortOrder: number;
  enabled: boolean;
  sourceCount: number;
  createdAt: string;
  updatedAt: string;
};

export type AdminVideoSource = {
  id: number;
  sourceType: string;
  displayName: string;
  upstreamName?: string;
  areaId?: number;
  areaName?: string;
  nvrDeviceId?: number;
  nvrDisplayName?: string;
  deviceId?: string;
  channelId?: string;
  mainId?: string;
  mediaServerId?: string;
  manualStreamUrlMasked?: string;
  isEnabled: boolean;
  onlineStatus: string;
  discoveryStatus: string;
  upstreamStatus: string;
  isActivated: boolean;
  supportsRecording: boolean;
  rtpTransport: string;
  defaultRtpPort: boolean;
  preferredProfile: string;
  hasPtz: boolean;
  sortOrder: number;
  notes?: string;
  lastSyncAt?: string;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type AdminNvrDevice = {
  id: number;
  deviceId: string;
  displayName: string;
  upstreamName?: string;
  onlineStatus: string;
  channelCount: number;
  importedCount: number;
  missingCount: number;
  lastSyncAt?: string;
  lastSeenAt?: string;
  lastErrorCode?: string;
  lastErrorMessage?: string;
  createdAt: string;
  updatedAt: string;
};

export type VideoCatalogSyncLog = {
  id: number;
  scope: string;
  nvrDeviceId?: number;
  triggerType: string;
  startedAt: string;
  finishedAt?: string;
  addedCount: number;
  updatedCount: number;
  missingCount: number;
  ignoredCount: number;
  errorCount: number;
  status: string;
  errorCode?: string;
  errorMessage?: string;
};

export type VideoCatalogSyncResult = {
  syncLogId: number;
  triggerType: string;
  initialImport: boolean;
  unclassifiedAreaCreated: boolean;
  unclassifiedAreaId: number;
  deviceCount: number;
  sourceCount: number;
  addedCount: number;
  updatedCount: number;
  missingCount: number;
  ignoredCount: number;
  areaCountAfter: number;
  nvrCountAfter: number;
  sourceCountAfter: number;
  startedAt: string;
  finishedAt: string;
};

export type UserSummary = {
  id: number;
  username: string;
  displayName: string;
  role: 'ADMIN' | 'USER';
  enabled: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type OverviewSummary = {
  totalVideoSources: number;
  onlineVideoSources: number;
  offlineVideoSources: number;
  disabledVideoSources: number;
  activeUpstreamStreams: number;
  liveViewerSessions: number;
  playbackSessions: number;
  activeUsers: number;
  latestMediaError?: string;
  latestSystemError?: string;
  generatedAt: string;
};

export type ServiceHealthItem = {
  key: string;
  displayName: string;
  status: string;
  reachable?: boolean;
  checkedAt: string;
  statusChangedAt: string;
  port?: number;
  version?: string;
  uptimeSeconds?: number;
  message?: string;
};

export type ProcessResourceStatus = {
  processId: number;
  name: string;
  cpuPercent?: number;
  workingSetBytes: number;
  uptimeSeconds: number;
};

export type SystemResourceStatus = {
  cpuPercent?: number;
  logicalProcessorCount: number;
  memoryUsedBytes?: number;
  memoryFreeBytes?: number;
  memoryTotalBytes?: number;
  diskUsedBytes?: number;
  diskFreeBytes?: number;
  diskTotalBytes?: number;
  diskName?: string;
  systemUptimeSeconds: number;
  apiProcess: ProcessResourceStatus;
  mediaServerProcess?: ProcessResourceStatus;
};

export type NetworkStatus = {
  adapterNames: string[];
  uploadBytesPerSecond: number;
  downloadBytesPerSecond: number;
  totalSentBytes: number;
  totalReceivedBytes: number;
  sampledAt: string;
};

export type VideoBusinessStatus = {
  totalVideoSources: number;
  onlineVideoSources: number;
  offlineVideoSources: number;
  disabledVideoSources: number;
  activeUpstreamStreams: number;
  startingUpstreamStreams: number;
  failedUpstreamStreams: number;
  liveViewerSessions: number;
  playbackSessions: number;
  activeUsers: number;
  enabledUsers: number;
  latestMediaError?: string;
  latestSystemError?: string;
  latestSystemErrorAt?: string;
};

export type AdminSystemStatus = {
  generatedAt: string;
  services: ServiceHealthItem[];
  resources: SystemResourceStatus;
  network: NetworkStatus;
  video: VideoBusinessStatus;
};
